import { useDispatch, useSelector } from 'react-redux';
import { UIHeading3, UIText1 } from '@cian/ui-kit';
import { useDialogConfirm } from '@cian/frontend-e2e-deal-component';

import {
  CommunicationActionButton,
  CommunicationImage,
  CommunicationLayout,
  ICommunicationImageProps,
  CommunicationInlineLink,
} from '../../components/Communication';
import { NBSP } from '../../constants/symbols';
import { becomeProfessional } from '../../actions/becomeProfessional';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { useAsyncState } from '../../utils/useAsyncState';

export const BECOME_PROFESSIONAL_IMAGE: ICommunicationImageProps['image'] = {
  x1: 'https://cdn.cian.site/frontend/e2e-form-assets/splash_screens/agent_x1.png',
  x2: 'https://cdn.cian.site/frontend/e2e-form-assets/splash_screens/agent_x2.png',
};

export const ERROR_COMMUNICATION_IMAGE: ICommunicationImageProps['image'] = {
  x1: 'https://cdn.cian.site/frontend/e2e-form-assets/splash_screens/error_x1.png',
  x2: 'https://cdn.cian.site/frontend/e2e-form-assets/splash_screens/error_x2.png',
};

const CIAN_MAIN_PAGE = 'https://www.cian.ru/';

export function BecomeProfessionalContainer() {
  const returnUrl = useSelector<IApplicationState, string | undefined>(state => state.page.returnUrl);
  const dispatch = useDispatch<TThunkDispatch>();

  const {
    state: [_, status],
    markAsResolved,
    markAsRejected,
    markAsPending,
    reset,
  } = useAsyncState<void>();
  const confirm = useDialogConfirm();

  const onConfirmStatusChange = () => {
    markAsPending();
    dispatch(becomeProfessional())
      .then(() => {
        markAsResolved();
        if (!returnUrl) {
          return;
        }

        window.location.replace(returnUrl);
      })
      .catch(error => markAsRejected(error));
  };

  const handleChangeStatus = () => {
    confirm({
      header: <UIHeading3>Точно хотите сменить аккаунт на{NBSP}профессиональный?</UIHeading3>,
      content: `Вернуть прежний можно только через${NBSP}службу поддержки`,
      acceptText: 'Да, хочу сменить',
      declineText: 'Отменить',
      onAccept: onConfirmStatusChange,
    });
  };

  const handleDontChangeStatus = () => {
    window.location.replace(document.referrer || CIAN_MAIN_PAGE);
  };

  if (status.isRejected) {
    return (
      <CommunicationLayout
        image={<CommunicationImage image={ERROR_COMMUNICATION_IMAGE} />}
        title="Ой, что-то пошло не так"
        message="Попробуйте снова или зайдите чуть позже"
        actions={<CommunicationActionButton onClick={reset}>Попробовать снова</CommunicationActionButton>}
        testId="become-professional-error"
        maxWidth="576px"
      />
    );
  }

  return (
    <CommunicationLayout
      image={<CommunicationImage image={BECOME_PROFESSIONAL_IMAGE} />}
      title={`Чтобы проводить сделки, нужен${NBSP}аккаунт${NBSP}профи — это бесплатно`}
      actions={
        <>
          <CommunicationActionButton
            onClick={handleChangeStatus}
            loading={status.isPending}
            testId="become-professional-btn"
          >
            Стать профи
          </CommunicationActionButton>
          <CommunicationActionButton
            onClick={handleDontChangeStatus}
            theme="fill_secondary"
            disabled={status.isPending}
            testId="dont-change-btn"
          >
            Не менять аккаунт
          </CommunicationActionButton>
        </>
      }
      testId="become-professional"
      maxWidth="576px"
    >
      <UIText1 textAlign="center">
        Сможете зарабатывать на{' '}
        <CommunicationInlineLink href="https://promo.cian.ru/profi" testId="promo-link">
          сервисах для профессионалов
        </CommunicationInlineLink>
        , но{NBSP}публиковать объявления как собственник не{NBSP}получится
      </UIText1>
    </CommunicationLayout>
  );
}
